import * as React from 'react'
import { upsertLink, validateUrl } from '@platejs/link'
import { LinkPlugin } from '@platejs/link/react'
import type { TLinkElement } from 'platejs'
import { KEYS } from 'platejs'
import { useEditorPlugin, useEditorRef } from 'platejs/react'

import { Button } from '../../../Button'
import { Input } from '../../../Input'
import { Popover } from '../../../Popover'
import { Text } from '../../../Text'
import { useRichTextFieldContext } from '../../RichTextField'

import styles from './LinkPopover.module.css'

type RichTextFieldLinkTarget = '_blank' | '_parent' | '_self' | '_top'

type LinkPopoverProps = {
  children: React.ReactElement
  target?: RichTextFieldLinkTarget
}

const LinkPopover = ({
  children,
  target: targetProp,
}: LinkPopoverProps): React.ReactElement => {
  const editor = useEditorRef()
  const { getOptions } = useEditorPlugin(LinkPlugin)
  const { disabled, readOnly } = useRichTextFieldContext()
  const [open, setOpen] = React.useState(false)
  const [url, setUrl] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const savedSelectionRef = React.useRef(editor.selection)
  const selectedLinkRef = React.useRef<TLinkElement | undefined>(undefined)
  const errorId = React.useId()
  const inputId = React.useId()
  const configuredTarget = getOptions().defaultLinkAttributes?.target
  const target =
    targetProp ??
    (configuredTarget === '_blank' ||
    configuredTarget === '_parent' ||
    configuredTarget === '_self' ||
    configuredTarget === '_top'
      ? configuredTarget
      : '_blank')

  React.useEffect(() => {
    if (disabled || readOnly) setOpen(false)
  }, [disabled, readOnly])

  const handleOpenChange = (nextOpen: boolean): void => {
    if (nextOpen) {
      savedSelectionRef.current = editor.selection
      selectedLinkRef.current = editor.api.above<TLinkElement>({
        match: { type: editor.getType(KEYS.link) },
      })?.[0]
      setError(null)
      setUrl(selectedLinkRef.current?.url ?? '')
    }

    setOpen(nextOpen)
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (disabled || readOnly) return

    const selection = savedSelectionRef.current
    const nextUrl = url.trim()

    if (!selection) return

    editor.tf.setSelection(selection)

    if (!validateUrl(editor, nextUrl)) {
      setError('Enter a safe URL.')
      return
    }

    upsertLink(editor, {
      skipValidation: true,
      target:
        targetProp ??
        (selectedLinkRef.current ? selectedLinkRef.current.target : target),
      url: nextUrl,
    })
    setOpen(false)
    editor.tf.focus()
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger>{children}</Popover.Trigger>
      <Popover.Content
        align="start"
        aria-label="Edit link"
        size="medium"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <form className={styles.form} onSubmit={handleSubmit}>
          <label className={styles.field} htmlFor={inputId}>
            <Text size="sm" weight="medium">
              URL
            </Text>
            <Input
              ref={inputRef}
              aria-describedby={error ? errorId : undefined}
              aria-invalid={error ? true : undefined}
              id={inputId}
              name="url"
              placeholder="https://example.com"
              size="small"
              value={url}
              onChange={(event) => {
                setUrl(event.target.value)
                setError(null)
              }}
            />
          </label>
          {error && (
            <Text id={errorId} as="p" color="critical" role="alert" size="sm">
              {error}
            </Text>
          )}
          <Button disabled={disabled || readOnly} size="small" type="submit">
            Apply link
          </Button>
        </form>
      </Popover.Content>
    </Popover>
  )
}

LinkPopover.displayName = 'RichTextField.LinkPopover'

export { LinkPopover }
export type { LinkPopoverProps, RichTextFieldLinkTarget }
